import {
  type AdditionalBillCostSubmission,
  type AdditionalBillCostType,
} from '@webapp/models'
import { type FeatureSwitches } from '@webapp/models'
import { ApiClient } from '../services/api'

export const SETTINGS_V3_URL = '/api/v3/settings'

/**
 * Company Finance API
 */
export const COMPANY_FINANCE_URL = `${SETTINGS_V3_URL}/company/finance`

export interface CompanyFinanceSettings {
  budgetOvertureToggle: boolean
}

export const fetchIsBudgetOverageEnabled = () =>
  ApiClient.get<CompanyFinanceSettings>(`${COMPANY_FINANCE_URL}/`, {
    params: {
      format: 'json',
    },
  })

export const toggleIsBudgetOverageEnabled = (data: CompanyFinanceSettings) =>
  ApiClient.patch<CompanyFinanceSettings>(`${COMPANY_FINANCE_URL}/`, data)

/**
 * Additional Bill Cost API
 */
export const ADDITIONAL_BILL_COST_URL = '/api/v2/ap/costs'

export const getAdditionalBillCosts = () =>
  ApiClient.get<AdditionalBillCostType[]>(`${ADDITIONAL_BILL_COST_URL}`)

export const createAdditionalBillCost = (cost: AdditionalBillCostSubmission) =>
  ApiClient.post(`${ADDITIONAL_BILL_COST_URL}/`, cost)

export const updateAdditionalBillCost = (
  costId: number,
  cost: AdditionalBillCostSubmission
) => ApiClient.put(`${ADDITIONAL_BILL_COST_URL}/${costId}/`, cost)

export const deleteAdditionalBillCost = (costId: number) =>
  ApiClient.delete(`${ADDITIONAL_BILL_COST_URL}/${costId}/`)

/**
 * Bill Field Customization API
 */
export const CUSTOMIZE_BILL_FIELDS_URL = `${SETTINGS_V3_URL}/customize-bill-fields`

export const customizeBillFields = (data: Partial<FeatureSwitches>) =>
  ApiClient.post(`${CUSTOMIZE_BILL_FIELDS_URL}/`, data)

export const updateRequiredInvoiceNumber = (value: boolean) =>
  ApiClient.post(`${CUSTOMIZE_BILL_FIELDS_URL}/`, {
    bill_field_invoice_number_required: value,
  })

/**
 * Bill Creation Settings API
 */

const VERIFIED_THREEWAY_MATCHING_URL = `${SETTINGS_V3_URL}/verified-three-way-matching/`

export const updateVerifiedThreewayMatching = (value: boolean) =>
  ApiClient.post(VERIFIED_THREEWAY_MATCHING_URL, {
    verified_three_way_matching: value,
  })

const USE_UNBILLED_PO_ITEM_DESCRIPTION_URL = `${SETTINGS_V3_URL}/use-unbilled-po-item-description/`

export const updateUseUnbilledPOItemDescription = (value: boolean) =>
  ApiClient.post(USE_UNBILLED_PO_ITEM_DESCRIPTION_URL, {
    use_unbilled_po_item_description: value,
  })

const DISABLE_AUTOMATED_MATCHING_IN_CREATE_BILL_URL = `${SETTINGS_V3_URL}/disable-automated-matching-in-create-bill/`

export const updateDisableAutomatedMatchingInCreateBill = (value: boolean) =>
  ApiClient.post(DISABLE_AUTOMATED_MATCHING_IN_CREATE_BILL_URL, {
    disable_automated_matching_in_create_bill: value,
  })

const RECEIVED_UNBILLED_LINE_COSTS = `${SETTINGS_V3_URL}/received-unbilled-line-costs/`

export const updateReceivedUnbilledLineCosts = (value: boolean) =>
  ApiClient.post(RECEIVED_UNBILLED_LINE_COSTS, {
    received_unbilled_line_costs: value,
  })

/**
 * Blanket order item settings API
 */
const BLANKET_ORDER_ITEM_URL = `${SETTINGS_V3_URL}/blanket-order-item/`

export const updateBlanketOrderItemSetting = (value: boolean) =>
  ApiClient.post<{ disabled: boolean }>(`${BLANKET_ORDER_ITEM_URL}`, {
    disabled: value,
  })

/**
 * Mandatory order description settings API
 */
const MANDATORY_ORDER_DESCRIPTION_URL = `${SETTINGS_V3_URL}/mandatory-order-description/`

export const updateMandatoryOrderDescriptionSetting = (value: boolean) =>
  ApiClient.post<{ enabled: boolean }>(`${MANDATORY_ORDER_DESCRIPTION_URL}`, {
    enabled: value,
  })

/**
 * Vendor approval settings API
 */
const VENDOR_APPROVAL_URL = `${SETTINGS_V3_URL}/vendor-approvals/`

export const updateVendorApprovalSetting = (value: boolean) =>
  ApiClient.post<{ enabled: boolean }>(`${VENDOR_APPROVAL_URL}`, {
    enabled: value,
  })

const SMART_CONTRACTS_URL = `${SETTINGS_V3_URL}/smart-contracts/`

export const updateSmartContractsSetting = (value: boolean) =>
  ApiClient.post<{ enabled: boolean }>(`${SMART_CONTRACTS_URL}`, {
    enabled: value,
  })
